import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { Package, Search, Filter, Grid, List, CheckCircle, XCircle, Pause, Play, Trash2, Eye, Star } from 'lucide-react';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [provinceFilter, setProvinceFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sellerFilter, setSellerFilter] = useState('all');
  const [sellerList, setSellerList] = useState([]);

  const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];

  useEffect(() => {
    loadProducts();
  }, [statusFilter, planFilter, provinceFilter, conditionFilter, sortBy, sellerFilter]);

  const loadProducts = async () => {
    try {
      let endpoint = '/products?limit=500';
      if (statusFilter !== 'all') endpoint = '/products?status=' + statusFilter + '&limit=500';
      
      const { data } = await api.get(endpoint);
      let allProducts = data.products || [];
      
      if (searchQuery) {
        allProducts = allProducts.filter(p => 
          p.title && p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.id && p.id.includes(searchQuery) ||
          p.seller && p.seller.name && p.seller.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      if (provinceFilter !== 'all') {
        allProducts = allProducts.filter(p => p.province === provinceFilter);
      }
      
      if (conditionFilter !== 'all') {
        allProducts = allProducts.filter(p => p.condition === conditionFilter);
      }
      
      if (planFilter !== 'all') {
        allProducts = allProducts.filter(p => p.seller && p.seller.subscription_plan === planFilter);
      }
      
      if (sellerFilter !== 'all') {
        allProducts = allProducts.filter(p => p.seller && p.seller.id === sellerFilter);
      }
      
      // Build seller list
      const sellers = {};
      allProducts.forEach(p => {
        if (p.seller && p.seller.id && !sellers[p.seller.id]) {
          sellers[p.seller.id] = p.seller;
        }
      });
      const sellerArr = Object.values(sellers);
      sellerArr.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
      });
      setSellerList(sellerArr);
      
      // Sort
      switch (sortBy) {
        case 'newest':
          allProducts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          break;
        case 'oldest':
          allProducts.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          break;
        case 'price_high':
          allProducts.sort((a, b) => b.price - a.price);
          break;
        case 'price_low':
          allProducts.sort((a, b) => a.price - b.price);
          break;
        case 'views':
          allProducts.sort((a, b) => (b.views || 0) - (a.views || 0));
          break;
      }
      
      setProducts(allProducts);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (productId, status) => {
    if (!confirm('¿' + (status === 'ACTIVE' ? 'Aprobar' : status === 'PAUSED' ? 'Pausar' : 'Rechazar') + ' este producto?')) return;
    
    try {
      await api.put('/products/' + productId, { status: status });
      loadProducts();
      setSelectedProduct(null);
    } catch (err) {
      alert('Error al actualizar producto');
    }
  };

  const deleteProduct = async (productId) => {
    if (!confirm('⚠️ ¿Estás seguro de ELIMINAR este producto? Esta acción no se puede deshacer.')) return;
    
    try {
      await api.delete('/products/' + productId);
      alert('Producto eliminado exitosamente');
      loadProducts();
      setSelectedProduct(null);
    } catch (err) {
      alert('Error al eliminar producto');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-700 border-green-300';
      case 'PENDING': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'PAUSED': return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'SOLD': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'DELETED': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return '✅ Activo';
      case 'PENDING': return '⏳ Pendiente';
      case 'PAUSED': return '⏸ Pausado';
      case 'SOLD': return '💰 Vendido';
      case 'DELETED': return '❌ Eliminado';
      default: return status;
    }
  };

  const countByStatus = (status) => {
    return products.filter(p => p.status === status).length;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Package className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Gestión de Productos</h1>
            <p className="text-gray-500 text-sm">{products.length} productos</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={'p-2 rounded-lg ' + (viewMode === 'grid' ? 'bg-primary text-white' : 'bg-gray-100')}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={'p-2 rounded-lg ' + (viewMode === 'list' ? 'bg-primary text-white' : 'bg-gray-100')}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, ID o vendedor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={'px-4 py-3 rounded-xl font-medium flex items-center gap-2 ' + (showFilters ? 'bg-primary text-white' : 'bg-gray-100')}
          >
            <Filter className="w-5 h-5" />
            Filtros
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Ordenar</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="newest">Más recientes</option>
                <option value="oldest">Más antiguos</option>
                <option value="price_high">Precio mayor</option>
                <option value="price_low">Precio menor</option>
                <option value="views">Más vistas</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Estado</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="all">Todos</option>
                <option value="ACTIVE">Activos</option>
                <option value="PENDING">Pendientes</option>
                <option value="PAUSED">Pausados</option>
                <option value="SOLD">Vendidos</option>
                <option value="DELETED">Eliminados</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Plan Vendedor</label>
              <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="all">Todos</option>
                <option value="PREMIUM">⭐ Premium</option>
                <option value="PRO">PRO</option>
                <option value="FREE">FREE</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Vendedor</label>
              <select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="all">Todos</option>
                {sellerList.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Provincia</label>
              <select value={provinceFilter} onChange={(e) => setProvinceFilter(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="all">Todas</option>
                {PROVINCES.map(p => (
                  <option key={p} value={p}>{p.replace('_', ' ')}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Condición</label>
              <select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="all">Todas</option>
                <option value="NEW">Nuevo</option>
                <option value="USED">Usado</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setStatusFilter('all');
                setPlanFilter('all');
                setProvinceFilter('all');
                setConditionFilter('all');
                setSellerFilter('all');
                setSortBy('newest');
                setSearchQuery('');
              }}
              className="text-sm text-gray-500 hover:text-red-500"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setStatusFilter('all')} className={'px-4 py-2 rounded-full font-medium ' + (statusFilter === 'all' ? 'bg-black text-white' : 'bg-gray-100')}>
          Todos <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{products.length}</span>
        </button>
        <button onClick={() => setStatusFilter('ACTIVE')} className={'px-4 py-2 rounded-full font-medium ' + (statusFilter === 'ACTIVE' ? 'bg-green-500 text-white' : 'bg-green-50')}>
          ✅ Activos <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('ACTIVE')}</span>
        </button>
        <button onClick={() => setStatusFilter('PENDING')} className={'px-4 py-2 rounded-full font-medium ' + (statusFilter === 'PENDING' ? 'bg-yellow-500 text-black' : 'bg-yellow-50')}>
          ⏳ Pendientes <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('PENDING')}</span>
        </button>
        <button onClick={() => setStatusFilter('PAUSED')} className={'px-4 py-2 rounded-full font-medium ' + (statusFilter === 'PAUSED' ? 'bg-gray-500 text-white' : 'bg-gray-50')}>
          ⏸ Pausados <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('PAUSED')}</span>
        </button>
        <button onClick={() => setStatusFilter('SOLD')} className={'px-4 py-2 rounded-full font-medium ' + (statusFilter === 'SOLD' ? 'bg-blue-500 text-white' : 'bg-blue-50')}>
          💰 Vendidos <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('SOLD')}</span>
        </button>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map(p => (
            <div key={p.id} className={'bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition border-2 ' + (p.status === 'DELETED' ? 'opacity-50' : '') + (p.status === 'PENDING' ? ' border-yellow-400' : ' border-transparent')}>
              <div className="relative aspect-square bg-gray-100">
                {p.images && p.images[0] && p.images[0].url ? (
                  <img src={p.images[0].url} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl">🛞</div>
                )}
                
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {p.condition === 'NEW' && (
                    <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded font-bold">Nuevo</span>
                  )}
                  {p.seller && p.seller.subscription_plan === 'PREMIUM' && (
                    <span className="bg-yellow-400 text-black text-xs px-2 py-0.5 rounded font-bold">⭐ PREM</span>
                  )}
                  {p.seller && p.seller.subscription_plan === 'PRO' && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded font-bold">PRO</span>
                  )}
                </div>
                
                <div className="absolute top-2 right-2">
                  <span className={'px-2 py-0.5 rounded text-xs font-bold border ' + getStatusBadge(p.status)}>
                    {getStatusLabel(p.status)}
                  </span>
                </div>
                
                {(p.seller && (p.seller.subscription_plan === 'PRO' || p.seller.subscription_plan === 'PREMIUM')) && (
                  <div className="absolute bottom-2 right-2">
                    <span className="bg-yellow-500 text-black w-8 h-8 rounded-full flex items-center justify-center font-bold">⭐</span>
                  </div>
                )}
              </div>
              
              <div className="p-4">
                <h3 className="font-semibold text-sm line-clamp-2 mb-1">{p.title}</h3>
                <p className="text-xs text-gray-500 mb-2">{p.category ? p.category.brand + ' ' + p.category.model : ''}</p>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-primary">₡{Number(p.price).toLocaleString()}</span>
                  {p.negotiable && <span className="text-xs text-gray-400">Negociable</span>}
                </div>
                
                <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 text-xs font-bold">
                    {p.seller && p.seller.name ? p.seller.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <span className="text-xs text-gray-600 truncate">{p.seller ? p.seller.name : 'N/A'}</span>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <span>👁 {p.views || 0}</span>
                  <span>📷 {p.images ? p.images.length : 0}</span>
                  <span>{p.province ? p.province.replace('_', ' ') : ''}</span>
                </div>
                
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setSelectedProduct(p)} className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                    <Eye className="w-4 h-4" /> Ver
                  </button>
                  
                  {p.status === 'PENDING' && (
                    <button onClick={() => updateStatus(p.id, 'ACTIVE')} className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Aprobar
                    </button>
                  )}
                  
                  {p.status === 'ACTIVE' && (
                    <button onClick={() => updateStatus(p.id, 'PAUSED')} className="flex-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                      <Pause className="w-4 h-4" /> Pausar
                    </button>
                  )}
                </div>
                
                {(p.status === 'ACTIVE' || p.status === 'PAUSED' || p.status === 'SOLD') && (
                  <button onClick={() => deleteProduct(p.id)} className="w-full px-3 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                    🗑️ Eliminar
                  </button>
                )}
                
                <p className="text-xs text-gray-400 mt-2 text-center">
                  {new Date(p.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-semibold">Producto</th>
                  <th className="text-left p-4 font-semibold">Vendedor</th>
                  <th className="text-left p-4 font-semibold">Precio</th>
                  <th className="text-left p-4 font-semibold">Estado</th>
                  <th className="text-left p-4 font-semibold">Stats</th>
                  <th className="text-left p-4 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.map(p => (
                  <tr key={p.id} className={'hover:bg-gray-50 ' + (p.status === 'DELETED' ? 'opacity-50' : '')}>
                    <td className="p-4">
                      <Link to={'/products/' + p.id} className="flex items-center gap-3 hover:text-primary">
                        {p.images && p.images[0] && p.images[0].url ? (
                          <img src={p.images[0].url} alt={p.title} className="w-12 h-12 object-cover rounded-lg" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">🛞</div>
                        )}
                        <div>
                          <p className="font-medium line-clamp-1">{p.title}</p>
                          <p className="text-xs text-gray-500">{p.category ? p.category.brand + ' ' + p.category.model : ''}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                          {p.seller && p.seller.name ? p.seller.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.seller ? p.seller.name : 'N/A'}</p>
                          <span className={'text-xs px-1.5 py-0.5 rounded ' + (p.seller && p.seller.subscription_plan === 'PREMIUM' ? 'bg-yellow-100 text-yellow-700' : p.seller && p.seller.subscription_plan === 'PRO' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500')}>
                            {p.seller ? (p.seller.subscription_plan || 'FREE') : 'FREE'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 font-bold">₡{Number(p.price).toLocaleString()}</td>
                    <td className="p-4">
                      <span className={'px-2 py-1 rounded text-xs font-bold border ' + getStatusBadge(p.status)}>
                        {getStatusLabel(p.status)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-3 text-sm text-gray-500">
                        <span>👁 {p.views || 0}</span>
                        <span>📷 {p.images ? p.images.length : 0}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => setSelectedProduct(p)} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg">
                          <Eye className="w-4 h-4" />
                        </button>
                        {p.status === 'PENDING' && (
                          <button onClick={() => updateStatus(p.id, 'ACTIVE')} className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {p.status === 'ACTIVE' && (
                          <button onClick={() => updateStatus(p.id, 'PAUSED')} className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg">
                            <Pause className="w-4 h-4" />
                          </button>
                        )}
                        {(p.status === 'ACTIVE' || p.status === 'PAUSED' || p.status === 'SOLD') && (
                          <button onClick={() => deleteProduct(p.id)} className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg" title="Eliminar">
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {products.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay productos que coincidan con los filtros</p>
        </div>
      )}

      {selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-lg">Detalle del Producto</h3>
              <button onClick={() => setSelectedProduct(null)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                  {selectedProduct.images && selectedProduct.images[0] && selectedProduct.images[0].url ? (
                    <img src={selectedProduct.images[0].url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">🛞</div>
                  )}
                </div>
                
                <div>
                  <h4 className="font-bold text-xl mb-2">{selectedProduct.title}</h4>
                  <p className="text-sm text-gray-500 mb-4">{selectedProduct.category ? selectedProduct.category.brand + ' ' + selectedProduct.category.model : ''}</p>
                  
                  <p className="text-3xl font-bold text-primary mb-4">₡{Number(selectedProduct.price).toLocaleString()}</p>
                  
                  <div className="flex gap-2 mb-4">
                    <span className={'px-3 py-1 rounded-full text-sm font-medium border ' + getStatusBadge(selectedProduct.status)}>
                      {getStatusLabel(selectedProduct.status)}
                    </span>
                    {selectedProduct.condition === 'NEW' && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-500 text-white">Nuevo</span>
                    )}
                  </div>
                  
                  <div className="text-sm space-y-1">
                    <p><span className="text-gray-500">Provincia:</span> {selectedProduct.province ? selectedProduct.province.replace('_', ' ') : ''}</p>
                    <p><span className="text-gray-500">Negociable:</span> {selectedProduct.negotiable ? 'Sí' : 'No'}</p>
                    <p><span className="text-gray-500">Vistas:</span> {selectedProduct.views || 0}</p>
                    <p><span className="text-gray-500">Imágenes:</span> {selectedProduct.images ? selectedProduct.images.length : 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h5 className="font-semibold mb-3">Vendedor</h5>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                    {selectedProduct.seller && selectedProduct.seller.name ? selectedProduct.seller.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <p className="font-medium">{selectedProduct.seller ? selectedProduct.seller.name : 'N/A'}</p>
                    <p className="text-sm text-gray-500">{selectedProduct.seller ? selectedProduct.seller.email : ''}</p>
                    <span className={'inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold ' + (selectedProduct.seller && selectedProduct.seller.subscription_plan === 'PREMIUM' ? 'bg-yellow-100 text-yellow-700' : selectedProduct.seller && selectedProduct.seller.subscription_plan === 'PRO' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500')}>
                      {selectedProduct.seller ? (selectedProduct.seller.subscription_plan || 'FREE') : 'FREE'}
                    </span>
                  </div>
                </div>
              </div>
              
              {selectedProduct.description && (
                <div className="mb-6">
                  <h5 className="font-semibold mb-2">Descripción</h5>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-4">{selectedProduct.description}</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <a href={'/products/' + selectedProduct.id} target="_blank" rel="noopener noreferrer" className="flex-1 bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary-dark flex items-center justify-center gap-2">
                  <Eye className="w-4 h-4" />
                  Ver Producto
                </a>
                
                {selectedProduct.status === 'PENDING' && (
                  <>
                    <button onClick={() => updateStatus(selectedProduct.id, 'ACTIVE')} className="flex-1 bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Aprobar
                    </button>
                    <button onClick={() => updateStatus(selectedProduct.id, 'DELETED')} className="px-6 py-3 bg-red-100 text-red-600 rounded-xl font-medium hover:bg-red-200">
                      <XCircle className="w-4 h-4" />
                    </button>
                  </>
                )}
                
                {selectedProduct.status === 'ACTIVE' && (
                  <button onClick={() => updateStatus(selectedProduct.id, 'PAUSED')} className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-medium hover:bg-gray-600 flex items-center justify-center gap-2">
                    <Pause className="w-4 h-4" />
                    Pausar
                  </button>
                )}
                
                {selectedProduct.status === 'PAUSED' && (
                  <button onClick={() => updateStatus(selectedProduct.id, 'ACTIVE')} className="flex-1 bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 flex items-center justify-center gap-2">
                    <Play className="w-4 h-4" />
                    Activar
                  </button>
                )}
                
                {selectedProduct.status !== 'DELETED' && (
                  <button onClick={() => deleteProduct(selectedProduct.id)} className="px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 flex items-center justify-center gap-2">
                    🗑️ Eliminar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}